import { t } from "i18next";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import classNames from "classnames";
import ReactSwitch from "react-switch";
import { templateActions, templateStore } from "../templateStore";
import { useSnapshot } from "valtio";

function OptionsApk({
    selectedRows,
    type,
}: {
    selectedRows: any;
    type: "SiteWeb" | "Application";
}) {
    const schema: any = useSelector(
        (state: any) => state.global.entityDetails.franchise_schema
    );
    const mainColor = useSelector(
        (state: any) => state.global.infoUser.maincolor
    );
    const boutique =
        selectedRows[0]?.shopId && selectedRows[0]?.shopId !== ""
            ? schema + "_" + selectedRows[0].shopId
            : "Default";
    const snap = useSnapshot(templateStore);
    const path = React.useMemo(() => {
        return type === "Application"
            ? ["Application", "ChoixBoutique", boutique, "Settings"]
            : ["SiteWeb", "ChoixBoutique", "Boutique", boutique, "Settings"];
    }, [type, boutique]);
    const [activeTab, setActiveTab] = React.useState<number>(0);
    React.useEffect(() => {
        const ensureAllPaths = async () => {
            if (type === "Application")
                await templateActions.ensurePathExists(
                    ["Application", "ChoixBoutique", boutique, "Settings"],
                    {
                        Activer_Emplacement: false,
                        Emplacement_Obligatoire: false,
                        Desactiver_Choix_Horaire: true,
                        Activer_Saisir_Emplacement: false,
                    }
                );
            else
                await templateActions.ensurePathExists(
                    [
                        "SiteWeb",
                        "ChoixBoutique",
                        "Boutique",
                        boutique,
                        "Settings",
                    ],
                    {
                        Emplacement_Obligatoire: false,
                        Desactiver_Choix_Horaire: true,
                        Activer_Saisir_Emplacement: false,
                    }
                );
        };

        ensureAllPaths();

        // eslint-disable-next-line
    }, []);

    type LocationState = {
        Activer_Emplacement?: boolean;
        Emplacement_Obligatoire: boolean;
        Desactiver_Choix_Horaire: boolean;
        Activer_Saisir_Emplacement: boolean;
    };

    const getSocialMediaStateFromStore = (): LocationState => {
        const defaultData: LocationState =
            type === "Application"
                ? {
                      Activer_Emplacement: false,
                      Emplacement_Obligatoire: false,
                      Desactiver_Choix_Horaire: true,
                      Activer_Saisir_Emplacement: false,
                  }
                : {
                      Emplacement_Obligatoire: false,
                      Desactiver_Choix_Horaire: true,
                      Activer_Saisir_Emplacement: false,
                  };

        try {
            const pathh = ["template", ...path];
            let current = snap as any;
            for (const key of pathh) {
                if (current[key] === undefined) {
                    return defaultData;
                }
                current = current[key];
            }
            return current ? JSON.parse(JSON.stringify(current)) : defaultData;
        } catch (error) {
            console.error("Error getting social media state:", error);
            return defaultData;
        }
    };

    const [locationState, setLocationState] = useState<LocationState>(
        getSocialMediaStateFromStore
    );

    const handleUpdateActivation = React.useCallback(
        (field: keyof LocationState, value: boolean) => {
            setLocationState((prev) => {
                let newState = { ...prev, [field]: value };

                if (field === "Activer_Emplacement" && !value) {
                    newState = {
                        ...newState,
                        Emplacement_Obligatoire: false,
                        Activer_Saisir_Emplacement: false,
                    };
                    templateActions.updateTemplate(
                        ["template", ...path, "Emplacement_Obligatoire"],
                        false
                    );
                    templateActions.updateTemplate(
                        ["template", ...path, "Activer_Saisir_Emplacement"],
                        false
                    );
                }

                if (field === "Emplacement_Obligatoire" && !value) {
                    newState = {
                        ...newState,
                        Activer_Saisir_Emplacement: false,
                    };
                    templateActions.updateTemplate(
                        ["template", ...path, "Activer_Saisir_Emplacement"],
                        false
                    );
                }

                templateActions.updateTemplate(
                    ["template", ...path, field],
                    value
                );
                return newState;
            });
        },
        [path]
    );

    const SettingSwitch = ({
        label,
        field,
        state,
        desabled = false,
        onToggle,
        inverted = false,
    }: {
        label: string;
        field: keyof LocationState;
        state: LocationState;
        desabled: boolean;
        onToggle: (field: keyof LocationState, value: boolean) => void;
        inverted?: boolean;
    }) => (
        <div className="image-upload-section mt-2">
            <span
                className={`${desabled ? "not-allowed-icon__clz" : ""}`}
                style={{
                    pointerEvents: "none",
                    cursor: "default",
                    color: "#000",
                }}
            >
                {t(label)}
            </span>
            <ReactSwitch
                disabled={desabled}
                className="cac_icn_quickConnection"
                offColor="#faafb4"
                offHandleColor="#E30613"
                onColor="#c2eec4"
                onHandleColor="#34C38F"
                height={19}
                handleDiameter={24}
                uncheckedIcon={false}
                checkedIcon={false}
                checked={inverted ? !state[field] : !!state[field]}
                onChange={(checked) => {
                    if (!desabled)
                        onToggle(field, inverted ? !checked : checked);
                }}
            />
        </div>
    );

    const tabs = [
        {
            title: t("Options"),
            content: (
                <div className="banner-container mt-2">
                    <section className="text-section">
                        <p className="image-title">
                            {t("Location management")}
                        </p>
                        <div className="banner-images">
                            {type === "Application" && (
                                <SettingSwitch
                                    label="Enable location QR code scanning"
                                    field="Activer_Emplacement"
                                    state={locationState}
                                    desabled={false}
                                    onToggle={handleUpdateActivation}
                                />
                            )}

                            <SettingSwitch
                                label="Require location QR code scan"
                                field="Emplacement_Obligatoire"
                                state={locationState}
                                desabled={
                                    type === "Application"
                                        ? !locationState.Activer_Emplacement
                                        : false
                                }
                                onToggle={handleUpdateActivation}
                            />

                            <SettingSwitch
                                label="Enable manual location entry"
                                field="Activer_Saisir_Emplacement"
                                state={locationState}
                                desabled={
                                    type === "Application"
                                        ? !locationState.Activer_Emplacement ||
                                          !locationState.Emplacement_Obligatoire
                                        : !locationState.Emplacement_Obligatoire
                                }
                                onToggle={handleUpdateActivation}
                            />
                        </div>
                    </section>

                    <section className="text-section">
                        <p className="image-title">
                            {t("Order pickup time selection")}
                        </p>
                        <div className="banner-images">
                            <SettingSwitch
                                label="Allow selection of order pickup time"
                                field="Desactiver_Choix_Horaire"
                                state={locationState}
                                desabled={false}
                                onToggle={handleUpdateActivation}
                                inverted={true}
                            />
                        </div>
                    </section>
                </div>
            ),
            customClassName: "",
        },
    ];

    React.useEffect(() => {
        setLocationState(getSocialMediaStateFromStore());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snap]);
    function toggleNav(tab: number) {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    }
    return (
        <div className="d-flex justify-content-between">
            <div style={{ width: "100%" }}>
                <div style={{ borderBottom: "1px solid #ddd4d4" }}>
                    <Nav
                        tabs
                        style={{
                            backgroundColor: "white",
                            width: "50px",
                            display: "grid",
                            gridTemplateColumns: `repeat(${tabs.length}, 1fr)`,
                            border: "unset",
                        }}
                    >
                        {tabs.map((element, index) => (
                            <NavItem
                                key={index}
                                className={classNames(
                                    "pb-1",
                                    "d-flex align-items-center text-nowrap",
                                    {
                                        "disable-nav-item__clz":
                                            activeTab !== index,
                                    },
                                    element.customClassName
                                )}
                                style={{
                                    cursor: "pointer",
                                    borderBottom:
                                        activeTab === index
                                            ? `${mainColor} 3px solid`
                                            : "",
                                }}
                            >
                                <NavLink
                                    className={classNames("text-dark w-100", {
                                        "active customer-account-menu-nav-link-active__clz":
                                            activeTab === index,
                                        "customer-account-menu-nav-link-disable__clz":
                                            activeTab !== index,
                                    })}
                                    onClick={() => toggleNav(index)}
                                >
                                    <span
                                        className="px-2"
                                        style={{
                                            font:
                                                "normal normal 600 20px/27px Segoe UI",
                                        }}
                                    >
                                        {element.title}
                                    </span>
                                </NavLink>
                            </NavItem>
                        ))}
                    </Nav>
                </div>
                <TabContent activeTab={activeTab} className="h-100">
                    {tabs.map((element, index) => (
                        <TabPane
                            tabId={index}
                            key={index}
                            style={{ height: "90%" }}
                        >
                            {element.content}
                        </TabPane>
                    ))}
                </TabContent>
            </div>
        </div>
    );
}

export default OptionsApk;
